import React, { ChangeEvent, cloneElement, ReactElement, useContext, useEffect, useState} from "react";
import styles from "../AddEditAutoImportConfigurationsModal.module.css";
import { LRequiredInput } from "./CommonComponents/LRequiredInput";
import { FormGrid } from "./CommonComponents/FormGrid";
import { LFormInput } from "@loffa/gpp-modules";
import { AuthContext } from "../../../../../../../../context/AuthContext";
import {FileSelectorModeOption} from "../types/autoImportConfigContextTypes";
import { useAutoImportFormContext } from "../../Context/AutoImportFormContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {faExclamationTriangle, faFolderOpen, faLock, faNetworkWired, faQuestionCircle, faServer, faPlus} from "@fortawesome/free-solid-svg-icons";
import { AddAutoImportConnectionModal } from "../../../AutoImportConnections/AddAutoImportConnectionModal";
import { createPortal } from "react-dom";
import { Connection } from "@appTypes/autoImportConnection";
import { KendoComboBoxEvent } from "@appTypes/kendoTypes";
import { ConnectionTypeEnum } from "src/enums/connectionTypeEnum";

const GATEWAY_BASE_URL = process.env.REACT_APP_API_GATEWAY_BASE;

const fileSelectorModeOptions = [
  { label: "File Name", value: 1 },
  { label: "Regular Expression", value: 2 },
  { label: "Wild Card", value: 3 },
];
const ADD_CONNECTION_ID = "add_new_connection";

function SourceFileForm() {
  const [connections, setConnections] = useState<Connection[]>();
  const [showAddConnectionModal, setShowAddConnectionModal] = useState<boolean>(false);
  const { formData, updateStepData, setError } = useAutoImportFormContext();
  const { sourceFile } = formData;
  const authContext = useContext(AuthContext);

  const fetchData = async () => {
    try {
      const response = await fetch(
        `${GATEWAY_BASE_URL}/multitenancy/api/AutoImportConnections`,
        {
          headers: { Authorization: `Bearer ${authContext.authState().accessToken.accessToken}`},
        }
      );
      if (!response.ok) throw new Error("Failed to fetch connections.");
      const result: Connection[] = await response.json();
      setConnections(result);
      setError(null);
    } catch (error: any) {
      setError("Error loading data: " + error.message);
    }
  };
  useEffect(() => {
    fetchData();
  }, [authContext, setError]);

  const onConnectionSelect = (e: KendoComboBoxEvent) => {
    if (e.target.value && e.target.value.id === ADD_CONNECTION_ID) {
      setShowAddConnectionModal(true);
      return;
    }
    updateStepData("sourceFile", { connection: e.target.value as Connection });
  };

  const onFileSelectorModeSelect = (e: KendoComboBoxEvent) => {
    updateStepData("sourceFile", {
      fileSelectorMode: e.target.value as FileSelectorModeOption,
    });
  };

  //adds the option to create a connection in the combobox
  const getConnectionsData = () => {
    if (!connections) return [];
    const addOption = {
      id: ADD_CONNECTION_ID,
      description: "Add a connection",
      connectionTypeEnum: -1,
    } as Connection;
    
    return [addOption, ...connections];
  };

  //custom render for "Connection" combobox
  const itemRender = ( li: ReactElement<HTMLLIElement>, itemProps: { index: number; dataItem: Connection }) => {
    const { dataItem: conn } = itemProps;
    
    if (conn.id === ADD_CONNECTION_ID) {
      //option to add/create a connection directly in this step
      const content = (
        <div className={`${styles.connectionItemContainer} ${styles.addConnectionOption}`}>
          <div className={styles.connectionListTopRow}>
            <FontAwesomeIcon icon={faPlus} className={styles.icon} />
            <span className={styles.description}>{conn.description}</span>
          </div>
        </div>
      );
      return cloneElement(li, li.props, content);
    }
    
    let icon, typeLabel: string;

    switch (conn.connectionTypeEnum) {
      case ConnectionTypeEnum.NETWORK:
        icon = faNetworkWired;
        typeLabel = "Network";
        break;
      case ConnectionTypeEnum.FTP:
        icon = faServer;
        typeLabel = "FTP";
        break;
      case ConnectionTypeEnum.LOCAL_DIRECTORY:
        icon = faFolderOpen;
        typeLabel = "Local Directory";
        break;
      case ConnectionTypeEnum.SFTP:
        icon = faLock;
        typeLabel = "SFTP";
        break;
      default:
        icon = faQuestionCircle;
        typeLabel = "Unknown";
        break;
    }

    const content = (
      <div className={styles.connectionItemContainer}>
        <div className={styles.connectionListTopRow}>
          <FontAwesomeIcon icon={icon} className={styles.icon} />
          <span className={styles.description}>{conn.description} - </span>
          <span>{typeLabel}</span>
        </div>
        <div className={styles.connectionListBottomRow}>
          <span>Server: {conn.server}</span>
          {conn.port && <span>Port: {conn.port}</span>}
          <span>Path: {conn.rootPath}</span>
        </div>
      </div>
    );
    return cloneElement(li, li.props, content);
  };

  const inputs = [
    {
      colSpan: 12,
      render: () => (
        <div className={styles.comboBoxLabelAlign}>
          <LRequiredInput
            type="combobox"
            labelName="Connection"
            labelAlign="top"
            required
            comboBox={{
              data: getConnectionsData(),
              dataItemKey: "id",
              textField: "description",
              value: sourceFile.connection || null,
              onChange: onConnectionSelect,
              placeholder: "-- Select Connection --",
              itemRender: itemRender,
            }}
          />
        </div>
      ),
    },
    {
      colSpan: 12,
      render: () => (
        <LFormInput
          type="input"
          labelAlign="top"
          labelName="Additional Path"
          input={{
            name: "Additional Path",
            placeholder: "Enter Additional Path",
            value: sourceFile.additionalPath || "",
            onChange: (e: React.ChangeEvent<HTMLInputElement>) => updateStepData("sourceFile", { additionalPath: e.target.value })}}
        />
      ),
    },
    {
      colSpan: 6,
      render: () => (
        <div className={styles.comboBoxLabelAlign}>
          <LRequiredInput
            type="combobox"
            labelName="File Selector Mode"
            labelAlign="top"
            required
            comboBox={{
              data: fileSelectorModeOptions,
              dataItemKey: "value",
              textField: "label",
              value: sourceFile.fileSelectorMode || null,
              onChange: onFileSelectorModeSelect,
              placeholder: "-- Select a File Selector Mode --",
            }}
          />
        </div>
      ),
    },
    {
      colSpan: 6,
      render: () => (
        <LRequiredInput
          type="input"
          labelAlign="top"
          required
          labelName="File Selector"
          input={{
            name: "File Selector",
            placeholder: "Enter File Selector",
            value: sourceFile.fileSelector || "",
            onChange: (e: ChangeEvent<HTMLInputElement>) => updateStepData("sourceFile", { fileSelector: e.target.value })}}
        />
      ),
    },
    {
      colSpan: 12,
      render: () => (
        <div className={styles.checkboxInputAlign}>
          <LRequiredInput
            type="checkbox"
            required
            labelName="Delete Source File After Import Completes"
            checkbox={{
              value: sourceFile.deleteSourceFile,
              checked: sourceFile.deleteSourceFile,
              onChange: (e: any) => updateStepData("sourceFile", { deleteSourceFile: e.value })}}
          />
        </div>
      ),
    },
    {
      colSpan: 12,
      render: () => (
        <div className={styles.deleteNote}>
          <FontAwesomeIcon icon={faExclamationTriangle} className={styles.warningIcon}/>
          <span>
            <b>Note:</b> Auto-Import will attempt to delete the source file only
            if all output configurations were completed successfully. If there
            are not output configurations, the process will still attempt to
            delete the source file.
          </span>
        </div>
      ),
    },
  ];

  return (
    <>
      <FormGrid items={inputs} />
      {/* allows users to create a new connection directly within the form */}
      {showAddConnectionModal && createPortal(
        <AddAutoImportConnectionModal
          isOpen={showAddConnectionModal}
          onClose={() => setShowAddConnectionModal(false)}
          onSave={() => {
            fetchData();
            setShowAddConnectionModal(false);
          }}
        />,
        document.body
      )}
    </>
  );
}

export { SourceFileForm };